/**
 * After a completed registration, turn a coach without a TourneyNation
 * account (embed-only "stub") into one when they kept "Save my team for
 * next time" checked. Runs once. A coach who already has an account is
 * left alone (no message). Failures are non-fatal: the registration and
 * receipt already went through.
 */
import { useEffect, useState } from 'react';

import type { EmbedClient } from '@/api/embed';

export type AccountStatus = 'idle' | 'pending' | 'created' | 'collision' | 'error';

export function useAccountPromotion(client: EmbedClient, shouldPromote: boolean) {
  const [status, setStatus] = useState<AccountStatus>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!shouldPromote || started) return;
    setStarted(true);
    client.getMe()
      .then((me) => {
        if (me.account_status !== 'stub') return; // already has an account
        setStatus('pending');
        return client.promoteToAccount()
          .then((resp) => {
            setStatus('created');
            setMessage(resp.message);
          })
          .catch((err) => {
            if (err?.response?.status === 409) {
              setStatus('collision');
              setMessage(
                err?.response?.data?.detail
                ?? 'An account already exists for your email — sign in there to claim your team.',
              );
            } else {
              setStatus('error');
              setMessage(
                'We couldn\'t create your account right now. You can sign up later '
                + 'at tourneynation.com using the email on your receipt.',
              );
            }
          });
      })
      .catch(() => { /* can't tell: leave it; the registration is done */ });
  }, [client, shouldPromote, started]);

  return { status, message };
}

/** The line the done screens show for the account step. */
export function accountNotice(status: AccountStatus, message: string | null): string | null {
  switch (status) {
    case 'pending': return 'Creating your TourneyNation account…';
    case 'created': return message ?? 'TourneyNation account created. Sign in any time using the email on your receipt.';
    case 'collision': return message ?? 'An account already exists for your email — sign in there.';
    case 'error': return message ?? 'Account creation didn\'t complete; your registration is still good.';
    default: return null;
  }
}
